import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FiltersByGroup } from "./thunks/fetchGenericFilters";

// Types de base
export interface TableState<T = any> {
  data: T[];
  loading: boolean;
  loadingFilters: boolean;
  error: string | null;
  // Pagination
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  // Recherche et filtres
  searchString: string;
  filters: Record<string, any>;
  // Tri
  sortField: string | null;
  sortDirection: 'asc' | 'desc' | null;
  // Selection
  selectedItems: Set<string | number>;
  // Cache
  lastFetchParams: string | null;
  filtersReferential: FiltersByGroup[];
}

export interface FetchDataParams {
  pageNumber: number;
  pageSize: number;
  searchString?: string;
  sorting?: string;
  filters?: Record<string, any>;
}

export interface TableResponse<T> {
  items: T[];
  totalItems: number;
  totalPages: number;
  currentPage: number;
}

// Configuration API pour chaque entité
export interface TableApiConfig<T> {
  fetchData: (params: FetchDataParams) => Promise<TableResponse<T>>;
  fetchFilters?: () => Promise<FiltersByGroup[]>;
}

// État global du slice
interface TablesState {
  currentEntity: string | null;
  entities: Record<string, TableState<any>>;
}

// État initial pour une entité
const createInitialState = <T>(): TableState<T> => ({
  data: [],
  loading: false,
  loadingFilters: false,
  error: null,
  currentPage: 1,
  pageSize: 10,
  totalItems: 0,
  totalPages: 0,
  searchString: '',
  filters: {},
  sortField: null,
  sortDirection: null,
  selectedItems: new Set(),
  lastFetchParams: null,
  filtersReferential: []
});

// Thunks génériques
export const createFetchDataThunk = <T>(entityType: string, apiConfig: TableApiConfig<T>) => {
  return createAsyncThunk<
    { entityType: string; response: TableResponse<T> },
    { entityType: string; params: FetchDataParams }
  >(
    `tables/${entityType}/fetchData`,
    async ({ entityType, params }) => {
      const response = await apiConfig.fetchData(params);
      return { entityType, response };
    }
  );
};

export const createFetchFiltersThunk = <T>(entityType: string, apiConfig: TableApiConfig<T>) => {
  if (!apiConfig.fetchFilters) {
    throw new Error(`fetchFilters not configured for entity ${entityType}`);
  }

  return createAsyncThunk<
    { entityType: string; filters: FiltersByGroup[] },
    { entityType: string }
  >(
    `tables/${entityType}/fetchFilters`,
    async ({ entityType }) => {
      const filters = await apiConfig.fetchFilters!();
      return { entityType, filters };
    }
  );
};

// Slice principal
const tablesSlice = createSlice({
  name: 'tables',
  initialState: {
    currentEntity: null,
    entities: {}
  } as TablesState,
  reducers: {
    // Gestion des entités
    setCurrentEntity: (state, action: PayloadAction<string>) => {
      state.currentEntity = action.payload;
      if (!state.entities[action.payload]) {
        state.entities[action.payload] = createInitialState();
      }
    },

    // Actions génériques avec entityType
    setCurrentPage: (state, action: PayloadAction<{ entityType: string; page: number }>) => {
      const { entityType, page } = action.payload;
      if (!state.entities[entityType]) state.entities[entityType] = createInitialState();
      state.entities[entityType].currentPage = page;
    },

    setPageSize: (state, action: PayloadAction<{ entityType: string; size: number }>) => {
      const { entityType, size } = action.payload;
      if (!state.entities[entityType]) state.entities[entityType] = createInitialState();
      state.entities[entityType].pageSize = size;
      state.entities[entityType].currentPage = 1;
    },

    setSearchString: (state, action: PayloadAction<{ entityType: string; search: string }>) => {
      const { entityType, search } = action.payload;
      if (!state.entities[entityType]) state.entities[entityType] = createInitialState();
      state.entities[entityType].searchString = search;
      state.entities[entityType].currentPage = 1;
    },

    clearSearch: (state, action: PayloadAction<{ entityType: string }>) => {
      const { entityType } = action.payload;
      if (!state.entities[entityType]) state.entities[entityType] = createInitialState();
      state.entities[entityType].searchString = '';
      state.entities[entityType].currentPage = 1;
    },

    setSorting: (state, action: PayloadAction<{ entityType: string; field: string; direction: 'asc' | 'desc' }>) => {
      const { entityType, field, direction } = action.payload;
      if (!state.entities[entityType]) state.entities[entityType] = createInitialState();
      state.entities[entityType].sortField = field;
      state.entities[entityType].sortDirection = direction;
      state.entities[entityType].currentPage = 1;
    },

    clearSorting: (state, action: PayloadAction<{ entityType: string }>) => {
      const { entityType } = action.payload;
      if (!state.entities[entityType]) state.entities[entityType] = createInitialState();
      state.entities[entityType].sortField = null;
      state.entities[entityType].sortDirection = null;
      state.entities[entityType].currentPage = 1;
    },

    setFilter: (state, action: PayloadAction<{ entityType: string; key: string; value: any }>) => {
      const { entityType, key, value } = action.payload;
      if (!state.entities[entityType]) state.entities[entityType] = createInitialState();
      state.entities[entityType].filters[key] = value;
      state.entities[entityType].currentPage = 1;
    },

    removeFilter: (state, action: PayloadAction<{ entityType: string; key: string }>) => {
      const { entityType, key } = action.payload;
      if (!state.entities[entityType]) state.entities[entityType] = createInitialState();
      delete state.entities[entityType].filters[key];
      state.entities[entityType].currentPage = 1;
    },

    clearFilters: (state, action: PayloadAction<{ entityType: string }>) => {
      const { entityType } = action.payload;
      if (!state.entities[entityType]) state.entities[entityType] = createInitialState();
      state.entities[entityType].filters = {};
      state.entities[entityType].currentPage = 1;
    },

    selectItem: (state, action: PayloadAction<{ entityType: string; id: string | number }>) => {
      const { entityType, id } = action.payload;
      if (!state.entities[entityType]) state.entities[entityType] = createInitialState();
      state.entities[entityType].selectedItems.add(id);
    },

    deselectItem: (state, action: PayloadAction<{ entityType: string; id: string | number }>) => {
      const { entityType, id } = action.payload;
      if (!state.entities[entityType]) state.entities[entityType] = createInitialState();
      state.entities[entityType].selectedItems.delete(id);
    },

    selectAllItems: (state, action: PayloadAction<{ entityType: string; ids: (string | number)[] }>) => {
      const { entityType, ids } = action.payload;
      if (!state.entities[entityType]) state.entities[entityType] = createInitialState();
      ids.forEach(id => state.entities[entityType].selectedItems.add(id));
    },

    clearSelection: (state, action: PayloadAction<{ entityType: string }>) => {
      const { entityType } = action.payload;
      if (!state.entities[entityType]) state.entities[entityType] = createInitialState();
      state.entities[entityType].selectedItems.clear();
    },

    resetTable: (state, action: PayloadAction<{ entityType: string }>) => {
      const { entityType } = action.payload;
      state.entities[entityType] = createInitialState();
    },

    // Action pour mise à jour directe de l'état
    updateEntityState: (state, action: PayloadAction<{ entityType: string; updates: Partial<TableState<any>> }>) => {
      const { entityType, updates } = action.payload;
      if (!state.entities[entityType]) state.entities[entityType] = createInitialState();
      Object.assign(state.entities[entityType], updates);
    },

    clearError: (state, action: PayloadAction<{ entityType: string }>) => {
      const { entityType } = action.payload;
      if (!state.entities[entityType]) state.entities[entityType] = createInitialState();
      state.entities[entityType].error = null;
    },
  },

  extraReducers: (builder) => {
    // Pattern générique pour les thunks de fetchData
    builder.addMatcher(
      (action) => action.type.endsWith('/fetchData/pending'),
      (state, action) => {
        const entityType = action.meta.arg.entityType;
        if (!state.entities[entityType]) state.entities[entityType] = createInitialState();
        state.entities[entityType].loading = true;
        state.entities[entityType].error = null;
      }
    );

    builder.addMatcher(
      (action) => action.type.endsWith('/fetchData/fulfilled'),
      (state, action: any) => {
        const { entityType, response } = action.payload;
        if (!state.entities[entityType]) state.entities[entityType] = createInitialState();

        const entityState = state.entities[entityType];
        entityState.loading = false;
        entityState.data = response.items;
        entityState.totalItems = response.totalItems;
        entityState.totalPages = response.totalPages;
        entityState.currentPage = response.currentPage;
        entityState.error = null;

        // Cache des paramètres de la dernière requête
        entityState.lastFetchParams = JSON.stringify({
          page: entityState.currentPage,
          pageSize: entityState.pageSize,
          search: entityState.searchString,
          filters: entityState.filters,
          sort: entityState.sortField,
          direction: entityState.sortDirection
        });
      }
    );

    builder.addMatcher(
      (action) => action.type.endsWith('/fetchData/rejected'),
      (state, action: any) => {
        const entityType = action.meta.arg.entityType;
        if (!state.entities[entityType]) state.entities[entityType] = createInitialState();

        const entityState = state.entities[entityType];
        entityState.loading = false;
        entityState.error = action.error.message || 'Une erreur est survenue';

        // Marquer les paramètres comme traités pour éviter les re-fetch automatiques
        entityState.lastFetchParams = JSON.stringify({
          page: entityState.currentPage,
          pageSize: entityState.pageSize,
          search: entityState.searchString,
          filters: entityState.filters,
          sort: entityState.sortField,
          direction: entityState.sortDirection
        });
      }
    );

    // Pattern générique pour les thunks de fetchFilters
    builder.addMatcher(
      (action) => action.type.endsWith('/fetchFilters/pending'),
      (state, action) => {
        const entityType = action.meta.arg.entityType;
        if (!state.entities[entityType]) state.entities[entityType] = createInitialState();
        state.entities[entityType].loadingFilters = true;
        state.entities[entityType].error = null;
      }
    );

    builder.addMatcher(
      (action) => action.type.endsWith('/fetchFilters/fulfilled'),
      (state, action: any) => {
        const { entityType, filters } = action.payload;
        if (!state.entities[entityType]) state.entities[entityType] = createInitialState();
        state.entities[entityType].loadingFilters = false;
        state.entities[entityType].filtersReferential = filters;
      }
    );

    builder.addMatcher(
      (action) => action.type.endsWith('/fetchFilters/rejected'),
      (state, action: any) => {
        const entityType = action.meta.arg.entityType;
        if (!state.entities[entityType]) state.entities[entityType] = createInitialState();

        const entityState = state.entities[entityType];
        entityState.loadingFilters = false;
        entityState.error = action.error.message || 'Une erreur est survenue';

        // Ne pas bloquer les futures tentatives de fetch de données
        // mais éviter les re-tentatives automatiques des filtres
      }
    );
  }
});

export const tablesActions = tablesSlice.actions;
export default tablesSlice.reducer;

// Sélecteurs génériques
export const createTableSelectors = <T>(entityType: string) => ({
  selectEntityState: (state: { tables: TablesState }) =>
    state.tables.entities[entityType] || createInitialState<T>(),

  selectData: (state: { tables: TablesState }) =>
    (state.tables.entities[entityType] || createInitialState<T>()).data,

  selectLoading: (state: { tables: TablesState }) =>
    (state.tables.entities[entityType] || createInitialState<T>()).loading,

  selectError: (state: { tables: TablesState }) =>
    (state.tables.entities[entityType] || createInitialState<T>()).error,

  selectPaginationInfo: (state: { tables: TablesState }) => {
    const entityState = state.tables.entities[entityType] || createInitialState<T>();
    return {
      currentPage: entityState.currentPage,
      totalPages: entityState.totalPages,
      totalItems: entityState.totalItems,
      pageSize: entityState.pageSize,
      startIndex: (entityState.currentPage - 1) * entityState.pageSize + 1,
      endIndex: Math.min(entityState.currentPage * entityState.pageSize, entityState.totalItems),
    };
  },

  selectSearchString: (state: { tables: TablesState }) =>
    (state.tables.entities[entityType] || createInitialState<T>()).searchString,

  selectFilters: (state: { tables: TablesState }) =>
    (state.tables.entities[entityType] || createInitialState<T>()).filters,

  selectFiltersReferential: (state: { tables: TablesState }) =>
    (state.tables.entities[entityType] || createInitialState<T>()).filtersReferential,

  selectSorting: (state: { tables: TablesState }) => {
    const entityState = state.tables.entities[entityType] || createInitialState<T>();
    return {
      field: entityState.sortField,
      direction: entityState.sortDirection
    };
  },

  selectSelectedItems: (state: { tables: TablesState }) =>
    (state.tables.entities[entityType] || createInitialState<T>()).selectedItems,

  selectShouldRefetch: (state: { tables: TablesState }) => {
    const entityState = state.tables.entities[entityType] || createInitialState<T>();

    if (entityState.error || entityState.loading || entityState.loadingFilters) {
      return false;
    }

    const currentParams = JSON.stringify({
      page: entityState.currentPage,
      pageSize: entityState.pageSize,
      search: entityState.searchString,
      filters: entityState.filters,
      sort: entityState.sortField,
      direction: entityState.sortDirection
    });

    return currentParams !== entityState.lastFetchParams;
  }
});
